import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { plural } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';

import SimplifiedPriceColumn from '@rover/components/src/cards/SearchResultCard/PriceColumn/SimplifiedPriceColumn';
import usePriceDisplayInfo from '@rover/components/src/cards/SearchResultCard/PriceColumn/usePriceDisplayInfo';
import { DEFAULT_CURRENCY_ISO } from '@rover/constants/src/i18n/language';
import { SearchParamsPetType } from '@rover/constants/src/translations/sitterProfilePage.constants';
import DropdownSmall from '@rover/icons/svg/dropdown-small.svg';
import { Box, Flex, Text } from '@rover/kibble/core';
import { Badge } from '@rover/kibble/official-patterns/Badge';
import useTheme from '@rover/kibble/themes/useTheme';
import { useApiBrightHorizonsSitterProfileBannerRetrieve } from '@rover/rsdk/src/apiClient/latest/api-bright-horizons-sitter-profile-banner-retrieve';
import { EnhancedPersonSitterProfileService } from '@rover/rsdk/src/apiClient/latest/model/enhancedPersonSitterProfileService';
import { PriceTransparencyLevels } from '@rover/types/src/PriceTransparencyLevel';

import { getParseCancellationDays } from '../../../../../utils/getParseCancellationDays';
import { FeeExplanationModal } from '../../../../search/SearchPage/components/Modals/FeeExplanationModal';
import {
  PtCondensedBanner,
  PtCondensedPetTypeDescription,
} from '../../../../search/SearchPage/components/PtCondensedBanner';
import { useRevampCta } from '../../hooks/useRevampCta';
import { useRevampCtaCommon } from '../../hooks/useRevampCtaCommon';
import { useRevampProfileContext } from '../../hooks/useRevampProfileContext';
import { BHSitterProfileBanner } from '../BHSitterProfileBanner/BHSitterProfileBanner';
import { SitterPriceBanner } from '../SitterPriceBanner';

import { AWAY_TEXT_COLOR, isServiceAway } from './awayService';
import RevampAdditionalRates from './RevampAdditionalRates';
import { CTAPropsCommon } from './RevampServicesCta';
import { serviceIconToGraphic } from './utils';

type Props = {
  handleOpenSheet: () => void;
  isOpen: boolean;
  cancellationPolicy?: string | undefined;
};

const RevampServicesCtaCommon = ({
  priceTransparencyLevel,
  locale,
  petType,
  petCount,
  bookingFeePercentage,
  handleOpenSheet,
  isOpen,
  cancellationPolicy,
}: CTAPropsCommon & Props): JSX.Element => {
  const { t } = useLingui();
  const theme = useTheme();
  const {
    sitterData,
    serviceType,
    selectedService,
    isBrightHorizonsUser,
    isSelectedServiceUnapproved,
  } = useRevampCta();
  const {
    countryConfigUrls,
    isPtFull,
    isPtCondensed,
    displayPetType,
    petTypeServices,
    rate,
    price,
  } = useRevampCtaCommon(priceTransparencyLevel, petType, petCount, selectedService);
  const [isFeeExplanationModalOpen, setIsFeeExplanationModalOpen] = useState(false);

  const { showSittersRates } = useRevampProfileContext();
  const priceInCredits = rate?.priceInCredits != null ? Number(rate.priceInCredits) : null;
  const shouldShowPriceInCredits =
    isBrightHorizonsUser && !showSittersRates && priceInCredits != null;

  const { slug = '' } = useParams<{ slug: string }>();
  const { data: brightHorizonsBanner } = useApiBrightHorizonsSitterProfileBannerRetrieve(
    {
      serviceType: serviceType || 'boarding',
      slug,
      petType: petType || SearchParamsPetType.DOG_CAT,
      dogCount: petCount.selectedDogCount,
      catCount: petCount.selectedCatCount,
      puppyCount: petCount.selectedPuppyCount,
    },
    { query: { enabled: isBrightHorizonsUser } }
  );

  const { formattedPrice, beforePriceText, afterPriceText } = usePriceDisplayInfo({
    currencyCode: rate?.pricePerUnitObj.currencyIso || DEFAULT_CURRENCY_ISO,
    priceTransparencyLevel,
    locale,
    price: price ?? '',
    priceInCredits,
    shouldShowPriceInCredits,
    showFromText: !shouldShowPriceInCredits,
    unitAsSentence: rate?.unitAsSentence ?? '',
    isBHUser: isBrightHorizonsUser,
  });

  const showingBH = !!brightHorizonsBanner?.bannerMessage;

  const renderServiceIcon = (iconKey: string, isAway: boolean): JSX.Element | null => {
    const IconComponent = serviceIconToGraphic[iconKey];
    if (!IconComponent) return null;
    return (
      <Box width="24px" height="24px" flexShrink="0">
        <IconComponent
          width="24"
          height="24"
          aria-hidden="true"
          fill={
            isAway ? theme.colors.text[AWAY_TEXT_COLOR] : theme.colors.interactive_text.primary_base
          }
        />
      </Box>
    );
  };

  const renderDropMenuItem = (service: EnhancedPersonSitterProfileService): JSX.Element => {
    const isAway = isServiceAway(service);
    return (
      <>
        <Text
          size="300"
          fontWeight="semibold"
          textColor={isAway ? AWAY_TEXT_COLOR : 'primary'}
          data-testid="cta-service-name"
        >
          {service.displayName}
        </Text>
        {service.description && (
          <Text size="200" textColor={isAway ? AWAY_TEXT_COLOR : 'secondary'}>
            {service.description}
          </Text>
        )}
      </>
    );
  };

  const getCancellationPolicyText = (): string | undefined => {
    const days = cancellationPolicy ? getParseCancellationDays(cancellationPolicy) : undefined;
    if (days === undefined) return undefined;
    if (days === 0) return t`Cancel <0>before</0> the booking begins.`.replace(/<\/?0>/g, '');
    const daysText = t({
      message: plural(days, { one: '# day', other: '# days' }),
    });
    return t`Cancel up to <0>${daysText}</0> before.`.replace(/<\/?0>/g, '');
  };

  const priceText =
    rate && price
      ? [beforePriceText, formattedPrice, afterPriceText].filter(Boolean).join(' ')
      : undefined;

  const serviceNameText = isServiceAway(selectedService)
    ? `${selectedService?.displayName} (${t`Away`})`
    : selectedService?.displayName;
  const serviceText = selectedService
    ? [serviceNameText, selectedService.description].filter(Boolean).join('. ')
    : t`Select a service`;

  const accessibilityMessage =
    [serviceText, priceText, getCancellationPolicyText()].filter(Boolean).join('. ') || undefined;

  return (
    <>
      <Flex
        data-testid="revamp-service-selector-trigger"
        width="100%"
        justifyContent="space-between"
        alignItems="center"
        onPress={handleOpenSheet}
        webAs="button"
        aria-label={accessibilityMessage}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        border="none"
        p="2x"
        sx={{
          background: 'transparent',
          cursor: 'pointer',
        }}
      >
        {(selectedService && (
          <Flex gap="3x">
            {renderServiceIcon(selectedService.icon, isServiceAway(selectedService))}
            <Flex flexDirection="column" alignItems="flex-start">
              {renderDropMenuItem(selectedService)}
            </Flex>
          </Flex>
        )) || <Text size="300" textColor="primary">{t`Select a service`}</Text>}

        <Box
          width="24px"
          height="24px"
          flexShrink="0"
          sx={{
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease',
          }}
          aria-hidden="true"
        >
          <DropdownSmall width="24" height="24" fill={theme.colors.text.primary} />
        </Box>
      </Flex>

      <Flex flexDirection="column" gap="2x" sx={{ paddingLeft: '44px' }}>
        {isSelectedServiceUnapproved ? (
          <Badge
            boxProps={{ testID: 'cta-coming-soon-badge', alignSelf: 'flex-start' }}
            text={t`Coming Soon`}
            variant="default"
          />
        ) : (
          rate &&
          price && (
            <SimplifiedPriceColumn
              currencyCode={rate.pricePerUnitObj.currencyIso}
              priceTransparencyLevel={priceTransparencyLevel}
              locale={locale}
              price={price}
              priceInCredits={priceInCredits}
              shouldShowPriceInCredits={shouldShowPriceInCredits}
              showFromText={!shouldShowPriceInCredits}
              unitAsSentence={rate?.unitAsSentence}
              isBHUser={isBrightHorizonsUser}
              variant="cta-desktop"
            />
          )
        )}

        {isPtCondensed && <PtCondensedPetTypeDescription petType={displayPetType} />}

        <RevampAdditionalRates
          services={sitterData.services}
          serviceType={serviceType}
          petType={petType}
          petCount={petCount}
        />
      </Flex>
      {showingBH && (
        <Box mb="-4x">
          <BHSitterProfileBanner brightHorizonsBanner={brightHorizonsBanner} />
        </Box>
      )}
      {isPtCondensed ? (
        <PtCondensedBanner
          priceTransparencyLevel={priceTransparencyLevel}
          bookingFeePercentage={bookingFeePercentage}
          onClick={() => setIsFeeExplanationModalOpen(true)}
          isSitterProfile
        />
      ) : (
        !showingBH &&
        isPtFull && (
          <SitterPriceBanner
            petCounts={petCount}
            petType={petType}
            serviceType={serviceType}
            countryConfigUrls={countryConfigUrls}
            bookingFeePercentage={bookingFeePercentage}
            services={petTypeServices}
            version="revamp"
          />
        )
      )}
      <FeeExplanationModal
        isOpen={isFeeExplanationModalOpen}
        onClose={() => setIsFeeExplanationModalOpen(false)}
        priceTransparencyLevel={priceTransparencyLevel || PriceTransparencyLevels.lite_condensed}
        bookingPercentageFee={bookingFeePercentage}
      />
    </>
  );
};

export default RevampServicesCtaCommon;
